import React, { useRef, useEffect, useState } from "react";
import MapStyles from "./Map.styles";
import { MapRefContainer, SetPinModeAction } from "./Map.types";
import { BaseMapService } from "./BaseMap.service";
import { useDispatch, useSelector } from "react-redux";
import ALERT_LIST from "../alert/AlertList.component";
import LEGEND from "./widgets/Legend/Legend.component";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { RootState } from "../../state-management/root-reducer";
import {
  setLoading,
  updateAdminModeState,
  updateDrawerState,
  updatePinModeState,
  updateSearchModeState,
} from "../../state-management/slices/global/global.slice";
import PinModeIcon from "../../theme/assets/map-controls/add_light_new.svg";
import PlusIcon from "../../theme/assets/map-controls/plus.svg";
import CheckStatusIcon from "../../theme/assets/map-controls/check_status.svg";
import LayersIcon from "../../theme/assets/map-controls/layers.svg";
import CloseIcon from "../../theme/assets/map-controls/close.svg";
import { AppDispatch } from "../../state-management/store";
import { FeatureLayerService } from "./FeatureLayer.service";
import MapView from "@arcgis/core/views/MapView";
import BUTTON from "../button/Button.component";
import LOADING_OVERLAY from "../loading/LoadingOverlay.component";
import ButtonStyles from "../button/Button.styles";
import { ClickEvent } from "../../shared/types/event.types";
import { useHistory } from "react-router-dom";
import { fetchAdminOutages } from "../../state-management/slices/outages/outages.thunk";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { AuthSericeEntra } from "../../shared/services/Auth.service.entra";

const togglePinMode = (
  pinModeActive: boolean,
  mapView: MapView,
  featureLayerService: FeatureLayerService,
  dispatch: AppDispatch,
  setPinMode: SetPinModeAction,
  sendDataToGTM: any
) => {
  if (pinModeActive) {
    if (mapView.popup.visible) {
      mapView.popup.close();
      featureLayerService.removePinModeGraphics();
    }

    featureLayerService.fadeOverlay("off");
    dispatch(setPinMode(false));
    sendDataToGTM({
      event: "eventTracking",
      category: "Outage",
      action: "Street Light",
      label: "Pin Mode Disabled",
    });
  } else {
    featureLayerService.buildOverlayLayer();
    featureLayerService.addOverlayLayer();
    featureLayerService.fadeOverlay("on");
    featureLayerService.buildPinModeLayer();
    dispatch(setPinMode(true));
    sendDataToGTM({
      event: "eventTracking",
      category: "Outage",
      action: "Street Light",
      label: "Pin Mode Enabled",
    });
  }
};

const BASE_MAP: React.FC<Record<string, unknown>> = () => {
  const dispatch = useDispatch();
  const { instance } = useMsal();
  const sendDataToGTM = useGTMDispatch();
  const mapContainer: MapRefContainer = useRef("");
  const basemapGalleryContainer: MapRefContainer = useRef("");
  const history = useHistory();

  // Services
  const authService = AuthSericeEntra.getInstance(dispatch, instance);
  const baseMapService: BaseMapService = BaseMapService.getInstance(
    dispatch,
    sendDataToGTM
  );

  // App State
  const { auth, appConfig, ui, locationServices } = useSelector(
    (state: RootState) => state.globalState
  );

  const isAuthenticated = useIsAuthenticated();
  const {
    baseMapInitialized,
    featureLayerInitialized,
    restrictedLayerInitialized,
    grandRapidsLayerInitialized,
    pinMode,
    loading,
  } = ui;

  // Local State
  const [pinModeActive, setPinModeActive] = useState(false);
  const [basemapToggleActive, setBasemapToggleActive] = useState(false);

  const locationContainerClassname = locationServices.locationDisabled
    ? "location-container esri-component esri-locate esri-widget--button esri-widget user-disabled"
    : "location-container esri-component esri-locate esri-widget--button esri-widget user-enabled";

  useEffect(() => {
    if (
      baseMapInitialized &&
      featureLayerInitialized &&
      restrictedLayerInitialized &&
      grandRapidsLayerInitialized &&
      loading
    ) {
      dispatch(setLoading(false));
    }

    if (pinModeActive !== pinMode.enabled) {
      setPinModeActive(pinMode.enabled);
    }

    if (mapContainer.current && basemapGalleryContainer.current) {
      if (appConfig && !baseMapInitialized) {
        baseMapService.initializeMapView(
          mapContainer,
          basemapGalleryContainer,
          appConfig
        );
      }
    }
  }, [
    appConfig,
    baseMapInitialized,
    baseMapService,
    dispatch,
    featureLayerInitialized,
    grandRapidsLayerInitialized,
    loading,
    pinMode.enabled,
    pinModeActive,
    restrictedLayerInitialized,
  ]);

  return (
    <>
      {/* --------------------------------- */}
      {/* MAP CONTAINER ------------------- */}
      {/* --------------------------------- */}
      <MapStyles.MapContainer
        id="MapContainer"
        className="map-container"
        ref={mapContainer}
      ></MapStyles.MapContainer>

      {/* --------------------------------- */}
      {/* SEARCH CONTROL CONTAINER -------- */}
      {/* --------------------------------- */}
      <MapStyles.SearchContainer
        id="searchContainer"
        className="search-container"
      ></MapStyles.SearchContainer>

      {/* --------------------------------- */}
      {/* PIN-MODE CONTROL CONTAINER ------ */}
      {/* --------------------------------- */}
      <MapStyles.PinModeContainer
        className={`pin-mode-container ${
          pinModeActive ? "active" : "inactive"
        }`}
      >
        <button
          type="button"
          className="pin-mode-button"
          title="Toggle Pin Mode"
          onClick={() => {
            togglePinMode(
              pinModeActive,
              baseMapService.getMapView(),
              baseMapService.getFeatureLayerService(),
              dispatch,
              updatePinModeState,
              sendDataToGTM
            );
          }}
        >
          <img src={PinModeIcon} alt="Pin Mode Icon" className="pin-icon" />
          <img src={PlusIcon} alt="Pin Mode Icon" className="plus-icon" />
        </button>
      </MapStyles.PinModeContainer>

      {/* --------------------------------- */}
      {/* BASEMAP TOGGLE CONTAINER -------- */}
      {/* --------------------------------- */}
      <MapStyles.BasemapToggleContainer
        className={`basemap-toggle-container ${
          basemapToggleActive ? "active" : "inactive"
        }`}
      >
        <button
          type="button"
          className="basemap-toggle-button"
          title="Basemap Selector"
          onClick={() => {
            setBasemapToggleActive(!basemapToggleActive);
            basemapToggleActive
              ? baseMapService.closeBasemapGallery()
              : baseMapService.openBasemapGallery();
          }}
        >
          {basemapToggleActive ? (
            <img src={CloseIcon} alt="Close Basemap Layers" />
          ) : (
            <img src={LayersIcon} alt="Open Basemap Layers" />
          )}
        </button>
      </MapStyles.BasemapToggleContainer>

      {/* --------------------------------- */}
      {/* BASEMAP GALLERY CONTAINER ------- */}
      {/* --------------------------------- */}
      <MapStyles.BasemapGalleryContainer
        id="BasemapGalleryContainer"
        className="basemap-gallery-container"
        ref={basemapGalleryContainer}
      ></MapStyles.BasemapGalleryContainer>

      {/* --------------------------------- */}
      {/* LOCATION CONTROL CONTAINER ------ */}
      {/* --------------------------------- */}
      <MapStyles.LocationContainer
        id="locationContainer"
        className={locationContainerClassname}
      ></MapStyles.LocationContainer>

      {/* --------------------------------- */}
      {/* ZOOM CONTROL CONTAINER ---------- */}
      {/* --------------------------------- */}
      <MapStyles.ZoomContainer
        id="zoomContainer"
        className="zoom-container"
      ></MapStyles.ZoomContainer>

      {/* ----------------------------------------- */}
      {/* CHECK OUTAGE STATUS CONTROL ------------- */}
      {/* ----------------------------------------- */}
      <MapStyles.SearchOutagesContainer
        className={`${pinModeActive ? "hidden" : ""} search-outages-container`}
      >
        {/* @todo: change to BlueSecondaryButton */}
        <BUTTON
          variant="secondary"
          className="lookup-button no-scale"
          onClick={(event) => {
            dispatch(updateSearchModeState(true));

            sendDataToGTM({
              event: "eventTracking",
              category: "Outage",
              action: "Street Light",
              label: "Check Outage Status Clicked",
            });
          }}
        >
          <img src={CheckStatusIcon} alt="Pin Mode Icon" />
          Check Outage Status
        </BUTTON>
      </MapStyles.SearchOutagesContainer>

      {/* ----------------------------------------- */}
      {/* ADMIN FOOTER ---------------------------- */}
      {/* ----------------------------------------- */}
      {auth.loggedIn && isAuthenticated && (
        <MapStyles.AdminFooterContainer className="admin-footer-container">
          <footer>
            <ButtonStyles.LinkButton
              className="link-button"
              onClick={(event: ClickEvent) => {
                event.preventDefault();
                dispatch(updateAdminModeState(true));
                dispatch(updateDrawerState(true));
                dispatch(fetchAdminOutages());
              }}
            >
              Open Admin Panel
            </ButtonStyles.LinkButton>
            |
            <ButtonStyles.LinkButton
              className="link-button"
              onClick={(event: ClickEvent) => {
                event.preventDefault();
                authService.signOut(history);
              }}
            >
              Logout
            </ButtonStyles.LinkButton>
          </footer>
        </MapStyles.AdminFooterContainer>
      )}

      {/* --------------------------------- */}
      {/* LEGEND CONTAINER ---------------- */}
      {/* --------------------------------- */}
      <LEGEND />

      {/* --------------------------------- */}
      {/* ALERT COMPONENT ----------------- */}
      {/* --------------------------------- */}
      <ALERT_LIST />

      {/* --------------------------------- */}
      {/* LOADING OVERLAY COMPONENT ------- */}
      {/* --------------------------------- */}
      {loading && <LOADING_OVERLAY show={true} />}
    </>
  );
};

export default BASE_MAP;
